import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MessageCircle, MapPin } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { WHATSAPP_LINK } from "@/data/constants";
import { useLang } from "@/context/LangContext";

const inputCls =
  "w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-sm text-[#0B132B] placeholder:text-slate-400 focus:outline-none focus:border-[#FFD166]";

export const ContactSection = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error(t("contact.errFields")); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.ok"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="py-16 md:py-24 bg-[#F5F7FB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <span className="text-[#E63946] text-sm font-semibold uppercase tracking-wider">{t("contact.eyebrow")}</span>
          <h2 className="font-head font-extrabold text-3xl md:text-5xl mt-2 text-[#0B132B]">{t("contact.title")}</h2>
          <div className="ej-accent-bar mt-4" />
          <p className="text-slate-600 mt-6">{t("contact.desc")}</p>
          <div className="space-y-4 mt-8">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
              className="flex items-center gap-3 text-slate-700 hover:text-[#128C7E] transition-colors">
              <span className="w-11 h-11 rounded-xl bg-[#25D366]/15 flex items-center justify-center"><MessageCircle className="text-[#128C7E]" size={20} /></span>
              <div><div className="text-xs text-slate-500">{t("contact.waLabel")}</div><div className="font-semibold">+48 512 287 255</div></div>
            </a>
            <div className="flex items-center gap-3 text-slate-700">
              <span className="w-11 h-11 rounded-xl bg-[#FFF6DC] flex items-center justify-center"><Mail className="text-[#B8860B]" size={20} /></span>
              <div><div className="text-xs text-slate-500">{t("contact.mailLabel")}</div><div className="font-semibold">contacto@europejobs.pro</div></div>
            </div>
            <div className="flex items-center gap-3 text-slate-700">
              <span className="w-11 h-11 rounded-xl bg-[#0B132B]/5 flex items-center justify-center"><MapPin className="text-[#0B132B]" size={20} /></span>
              <div><div className="text-xs text-slate-500">{t("contact.officeLabel")}</div><div className="font-semibold">{t("contact.office")}</div></div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl border border-slate-200 shadow-lg p-6 md:p-8 space-y-4">
          <input data-testid="contact-name" className={inputCls} placeholder={t("contact.name")} value={form.name} onChange={(e) => set("name", e.target.value)} />
          <input data-testid="contact-email" type="email" className={inputCls} placeholder={t("contact.email")} value={form.email} onChange={(e) => set("email", e.target.value)} />
          <input data-testid="contact-phone" className={inputCls} placeholder={t("contact.phone")} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          <textarea data-testid="contact-message" rows={4} className={inputCls} placeholder={t("contact.message")} value={form.message} onChange={(e) => set("message", e.target.value)} />
          <button type="submit" disabled={loading} data-testid="contact-submit"
            className="w-full bg-[#FFD166] text-[#0B132B] font-bold py-3.5 rounded-full hover:bg-[#E0A926] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 className="animate-spin" size={18} />} {t("contact.submit")}
          </button>
        </form>
      </div>
    </section>
  );
};
